
import {
  entitySlugForLocale,
  buildHreflangLinks,
  computeAlternateUrls,
} from '@/utils/hreflang'
import {
  hasListFilters,
  paginatedCanonical,
  paginatedTitle,
  paginationPage,
  withPageQuery,
  withPageSuffix,
} from '@/utils/paginationSeo'
import { buildOgLocaleMeta } from '@/utils/seo'
import BreadcrumbStructuredData from '@/components/BreadcrumbStructuredData'
import Breadcrumb from '@/components/Breadcrumb'
import Category from '@/components/Category'
import BreadcrumbMixin from '@/mixins/breadcrumb'

export default {
  name: 'DomainIndex',
  components: { Breadcrumb, BreadcrumbStructuredData, Category },
  mixins: [BreadcrumbMixin],

  data() {
    return {
      breadcrumb_list: [],
      domain: null,
      category: null,
      languageMap: {},
    }
  },
  async fetch() {
    await Promise.all([
      this.$getLanguageMap().then(map => {
        this.languageMap = map
      }),
      this.$categoryService
        .fetchCategoryPage(this.$route.params.category)
        .then(({ data }) => {
          this.category = data
          this.domain = data.nested_parents_light
          this.$store.commit(
            'SET_RESTRICTED_LOCALES',
            data.restricted_locales || []
          )

          if (
            this.$route.params.domain !== this.domain.slug ||
            this.$route.params.category !== this.category.slug
          ) {
            const correctPath = this.localePath({
              name: 'categories-domain-category',
              params: {
                domain: this.domain.slug,
                category: this.category.slug,
              },
            })
            if (process.server) {
              this.$nuxt.context.redirect(301, correctPath)
            } else {
              this.$router.replace(correctPath)
            }
            return
          }

          const breadcrumb = [
            {
              text: this.$t('pages.categories.domain.category.index.home'),
              to: this.localePath({ name: 'index' }),
            },
          ]
          if (this.domain) {
            breadcrumb.push({
              text: this.domain.name,
              to: this.localePath({
                name: 'categories-domain',
                params: { domain: this.$route.params.domain },
              }),
            })
          }
          breadcrumb.push({
            text: this.category.name,
            to: this.localePath({
              name: 'categories-domain-category',
              params: {
                domain: this.$route.params.domain,
                category: this.$route.params.category,
              },
            }),
          })
          this.breadcrumb_list = breadcrumb
        })
        .catch(error => {
          if (error?.response?.status === 451) {
            this.$451Error('category')
          } else {
            this.$404Error()
          }
        }),
    ])

    if (
      this.domain &&
      this.category &&
      this.$route.params.domain === this.domain.slug &&
      this.$route.params.category === this.category.slug
    ) {
      computeAlternateUrls({
        locales: this.$i18n.locales,
        localePath: this.localePath,
        routeName: 'categories-domain-category',
        buildParams: code => {
          if ((this.$store.state.restricted_locales || []).includes(code))
            return null
          const domain = entitySlugForLocale(this, this.domain, code)
          const category = entitySlugForLocale(this, this.category, code)
          return domain && category ? { domain, category } : null
        },
        store: this.$store,
      })
    }
  },
  head() {
    if (this.category && this.domain) {
      const canonicalUrl = paginatedCanonical(
        this.$config.url +
          this.localePath({
            name: 'categories-domain-category',
            params: {
              domain: this.domain.slug,
              category: this.category.slug,
            },
          }),
        this.$route.query
      )
      const hreflangLinks = buildHreflangLinks({
        locales: this.$i18n.locales,
        configUrl: this.$config.url,
        localePath: this.localePath,
        routeName: 'categories-domain-category',
        buildParams: code => {
          if ((this.$store.state.restricted_locales || []).includes(code))
            return null
          const domain = entitySlugForLocale(this, this.domain, code)
          const category = entitySlugForLocale(this, this.category, code)
          return domain && category ? { domain, category } : null
        },
        store: this.$store,
      })
      return {
        title: paginatedTitle(
          this.category.meta_title
            ? this.category.meta_title
            : this.category.name,
          this.$route.query,
          this.$t.bind(this)
        ),
        link: [
          {
            hid: 'i18n-can',
            rel: 'canonical',
            href: canonicalUrl,
          },
          ...withPageQuery(hreflangLinks, this.$route.query),
        ],
        meta: [
          ...(this.hasListFilters
            ? [
                {
                  hid: 'robots',
                  name: 'robots',
                  content: 'noindex, follow',
                },
              ]
            : []),
          {
            hid: 'description',
            name: 'description',
            content: withPageSuffix(
              this.category.meta_description
                ? this.category.meta_description
                : this.stripHtml(this.category.description_top),
              this.$route.query,
              this.$t.bind(this)
            ),
          },
          { hid: 'og:type', property: 'og:type', content: 'website' },
          {
            hid: 'og:url',
            property: 'og:url',
            content: canonicalUrl,
          },
          {
            hid: 'og:title',
            property: 'og:title',
            content: this.category.meta_title
              ? this.category.meta_title
              : this.category.name,
          },
          {
            hid: 'og:description',
            property: 'og:description',
            content: withPageSuffix(
              this.category.meta_description
                ? this.category.meta_description
                : this.stripHtml(this.category.description_top),
              this.$route.query,
              this.$t.bind(this)
            ),
          },
          {
            hid: 'og:site_name',
            property: 'og:site_name',
            content: 'AGM Vision',
          },
          ...buildOgLocaleMeta(this.$i18n.locale),
          {
            hid: 'og:image',
            property: 'og:image',
            content:
              this.category.images && this.category.images.length > 0
                ? this.category.images[0].url.startsWith('http')
                  ? this.category.images[0].url.replace(/ /g, '%20')
                  : `${this.$config.url}${this.category.images[0].url.replace(
                      / /g,
                      '%20'
                    )}`
                : '',
          },
          {
            hid: 'og:image:alt',
            property: 'og:image:alt',
            content: this.category.meta_title || this.category.name,
          },
          {
            hid: 'twitter:card',
            name: 'twitter:card',
            content: 'summary_large_image',
          },
          {
            hid: 'twitter:title',
            name: 'twitter:title',
            content: this.category.meta_title || this.category.name,
          },
          {
            hid: 'twitter:description',
            name: 'twitter:description',
            content: withPageSuffix(
              this.category.meta_description
                ? this.category.meta_description
                : this.stripHtml(this.category.description_top),
              this.$route.query,
              this.$t.bind(this)
            ),
          },
          {
            hid: 'twitter:image',
            name: 'twitter:image',
            content:
              this.category.images && this.category.images.length > 0
                ? this.category.images[0].url.startsWith('http')
                  ? this.category.images[0].url.replace(/ /g, '%20')
                  : `${this.$config.url}${this.category.images[0].url.replace(
                      / /g,
                      '%20'
                    )}`
                : '',
          },
        ],
      }
    }
  },
  computed: {
    siblingCategories() {
      if (!this.domain?.children) return []
      return this.domain.children.filter(c => c.id !== this.category.id)
    },
    hasListFilters() {
      return hasListFilters(this.$route.query)
    },
    isPaginated() {
      return paginationPage(this.$route.query) !== null
    },
  },
  methods: {
    stripHtml(html) {
      if (!html) return ''
      return html.replace(/<[^>]*>/g, '').trim()
    },
  },
}
